import {
  postVerificationCode,
  requestForVerificationCode,
  VerificationCode,
} from "@/libs/apis/onboarding/mfa";
import { Controller, FormProvider, get, useForm } from "react-hook-form";
import { logger } from "@/libs/utils";
import { InputWrapper } from "./InputWrapper";
import {
  GENERIC_ERROR_MESSAGE,
  INVALID_VERIFICATION_NUMBER_ERROR,
  convertStatusCodeToErrorMessage,
} from "../constants";
import { PhoneNumber } from "@/libs/apis/onboarding/mfa";
import { isValidElement, Children, cloneElement } from "react";
import { Body5 } from "@/src/components/typography/components/Body";
import { ALLOWED_CHARACTER_VALUES } from "@/src/components/form/input/MFAInput/types";
import clsx from "clsx";
import useCurrentUserEmail from "@/src/services/auth/useCurrentUserEmail";
import { useMFAContext } from "../contexts/MFAContextProvider";
import ResendVerificationCodeText from "@/src/components/cards/components/ProductCard/components/ProductCardFooter/components/TokenTransferModule/TokenTransferModal/components/steps/VerifyAccount/MFAView/components/ResendVerificationCode";
import VerificationCodeInput from "nvs-design-system/dist/MFA/VerificationCodeInput";

type FormValues = VerificationCode;

type Props = React.PropsWithChildren<{
  onSubmit?: (
    data: VerificationCode & { phoneNumber: PhoneNumber },
  ) => Promise<void>;
  onSendAgain?: (data?: FormValues) => Promise<void>;
  onEditNumber?: () => void;
  classes?: {
    form?: string;
    header?: string;
    formBody?: string;
  };
}>;

const EnterVerification: React.FC<Props> = ({
  onSubmit,
  children,
  classes,
  onSendAgain,
}) => {
  const {
    phoneNumber,
    verificationMethod,
    verificationEntity,
    setMfaId,
    mfaId,
  } = useMFAContext();

  const currentUserEmail = useCurrentUserEmail();

  const methods = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      verificationCode: "",
    },
  });

  const {
    handleSubmit,
    control,
    setError,
    getValues,
    formState: { isSubmitting, isDirty, isValid },
  } = methods;

  const handleError = (message: string) => {
    setError(
      "verificationCode",
      { message: message, type: "focus" },
      { shouldFocus: true },
    );
  };
  const submitHandler = async (data: FormValues) => {
    try {
      await postVerificationCode({ ...data, mfaId });
      try {
        if (onSubmit) await onSubmit({ ...data, phoneNumber });
      } catch (e) {
        handleError(GENERIC_ERROR_MESSAGE);
      }
    } catch (e) {
      handleError(INVALID_VERIFICATION_NUMBER_ERROR);
    }
  };

  const handleSendAgain = async () => {
    try {
      const mfaId = await requestForVerificationCode(verificationMethod);
      setMfaId(mfaId);
      onSendAgain &&
        (await onSendAgain({
          verificationCode: getValues("verificationCode"),
        }));
    } catch ({ response }) {
      const statusCode = get(response, "status");
      const message = convertStatusCodeToErrorMessage(statusCode);
      handleError(message);
      logger.debug(response);
    }
  };

  const childrenWithProps = Children.map(children, (child) => {
    if (!isValidElement(child)) return null;
    return cloneElement(child, {
      ...child.props,
      isLoading: isSubmitting,
      disabled: !isDirty || !isValid || isSubmitting,
    });
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)} className={classes?.form}>
        <div className={clsx(classes?.formBody, "flex flex-col gap-6")}>
          <Body5 className={clsx(classes?.header)}>
            {`Enter the six digit verification code we sent to ${verificationEntity}.`}
          </Body5>
          <InputWrapper>
            <Controller
              name="verificationCode"
              control={control}
              render={({ field: { onChange, ref }, fieldState: { error } }) => {
                return (
                  <VerificationCodeInput
                    allowedCharacters={ALLOWED_CHARACTER_VALUES.NUMERIC}
                    hasError={!!error?.message}
                    length={6}
                    ref={ref}
                    placeholder="0"
                    errorMessage={error?.message}
                    onChange={onChange}
                  >
                    <ResendVerificationCodeText onResend={handleSendAgain} />
                  </VerificationCodeInput>
                );
              }}
              rules={{
                required: true,
                minLength: 6,
                maxLength: 6,
              }}
            />
          </InputWrapper>
        </div>
        {childrenWithProps}
      </form>
    </FormProvider>
  );
};

export default EnterVerification;
